import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { UsersApi } from '@/api/clients/users/users.api';
// core components
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridsItem';
import { validatePassword } from './password.utils';

import logoimg from '@/assets/img/imgpsh_fullsize_anim_front.png';
import ImageLoader from '@/components/CustomImage/CustomImage';
import loader from '../../assets/img/loader.svg';
import FloatingLanguagePicker from '@/components/LanguagePicker/FloatingLanguagePicker';
import PoweredByTailfin from '@/components/PoweredByTailfin';
import { toast } from 'react-toastify';

const ActivateUser = () => {
  const { t } = useTranslation();
  const [encodedUser, setEncodedUser] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showLoading, setShowLoading] = useState(true);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [redirectTo, setRedirectTo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { encoded } = useParams();

  useEffect(() => {
    decodeToken();
  }, []);

  const handlePassword = (event) => {
    setPassword(event.target.value);
  };

  const handleOtp = (event) => {
    setOtp(event.target.value);
  };

  const decodeToken = async () => {
    setIsLoading(true);
    const encodedUser = encoded;
    setEncodedUser(encodedUser);
    setRedirect(false);

    try {
      const res = await UsersApi.activateUser(encodedUser);

      if (res.status === 200) {
        setEmail(res.data.email);
        setShowLoading(false);
        setShowSetPassword(true);
        setIsLoading(false);

        localStorage.removeItem('appState');
        localStorage.removeItem('lastActivity');
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
      setIsLoading(false);
    }
  };

  const updatePassword = async (e) => {
    e.preventDefault();

    const validation = validatePassword(password);

    if (!validation.isValid) {
      toast.error(t(validation.error));
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('token', encodedUser);
    formData.append('password', password);

    try {
      const res = await UsersApi.updatePassword(formData);

      if (res.status === 200) {
        setShowSetPassword(false);
        setRedirectTo(res.data.redirectUrl);
        setErrorMessage('');

        setIsLoading(false);
        if (!res.data.mfaSetupRequired) {
          setRedirect(true);
        } else {
          setShowOTP(true);
          getOtpQrCode();
        }
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  const getOtpQrCode = async () => {
    try {
      const res = await UsersApi.getOtpQrCode();
      if (res.status === 200) {
        setQrCode(res.data.qrCode);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('totp', otp);

    try {
      const res = await UsersApi.verifyOTP(formData);
      if (res.status === 200) {
        setRedirect(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}>
          <img src={loader} alt='Loading...' />
        </div>
      )}

      {redirect && <Navigate to={redirectTo} />}
      <GridContainer>
        <GridItem xs={12} sm={10} md={6} id='10'>
          <Card>
            <CardHeader color='primary'>
              <GridContainer alignItems='center' justify='space-between'>
                <GridItem xs={8} sm={8} md={6} className='mx-auto'>
                  <h4>{t('auth.activateUser.title')}</h4>
                </GridItem>
                <GridItem xs={8} sm={8} md={6}>
                  <img src={logoimg} width='130' />
                </GridItem>
              </GridContainer>
            </CardHeader>
            <CardBody>
              {showLoading && (
                <GridContainer>
                  <GridItem
                    xs={8}
                    sm={8}
                    md={6}
                    style={{ textAlign: 'center' }}>
                    <h3>
                      {errorMessage.length > 0 ? (
                        <b>{errorMessage}</b>
                      ) : (
                        t('common.labels.loading')
                      )}
                    </h3>
                  </GridItem>
                </GridContainer>
              )}
              {showSetPassword && (
                <form onSubmit={updatePassword}>
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <CustomInput
                        labelText={t('auth.activateUser.usernameLabel')}
                        id='email'
                        type='email'
                        name='email'
                        value={email}
                        formControlProps={{
                          fullWidth: true,
                        }}
                        disabled={true}
                      />
                      <CustomInput
                        labelText={t('auth.activateUser.setPasswordLabel')}
                        id='password'
                        type='password'
                        name='password'
                        onChange={handlePassword}
                        value={password}
                        formControlProps={{
                          fullWidth: true,
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <Button color='primary' type='submit'>
                        {t('auth.activateUser.saveButton')}
                      </Button>
                    </GridItem>
                  </GridContainer>
                </form>
              )}
              {showOTP && (
                <form onSubmit={verifyOTP}>
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <h4>{t('auth.activateUser.qrCodeInstructions')}</h4>
                      <ImageLoader src={qrCode} alt='QR Code' />
                      <h4>{t('auth.activateUser.otpInstructions')}</h4>
                      <CustomInput
                        labelText={t('auth.activateUser.otpLabel')}
                        id='otp'
                        type='text'
                        name='otp'
                        onChange={handleOtp}
                        value={otp}
                        formControlProps={{
                          fullWidth: true,
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <Button color='primary' type='submit'>
                        {t('auth.activateUser.sendButton')}
                      </Button>
                    </GridItem>
                  </GridContainer>
                </form>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}>
        <PoweredByTailfin size='medium' />
      </div>

      <FloatingLanguagePicker />
    </>
  );
};

export default ActivateUser;
